import { StormBuildUpRowDef } from './stormBuildUpTypes';

export const getBoxCulvertRowSuffixes = (): string[] => (
  ['height', 'excavate', 'earthwork', 'compact', 'deposit', 'concBlinding', 'bedTop', 'bedSide', 'backfill', 'purchase', 'labour']
);

// Per-culvert configurable field, same registry shape as stormBuildUpPonds.ts's
// POND_CONFIGURABLE_FIELDS — only one field today (culvert height), kept as a
// one-entry array/lookup so the Configure modal and row-id parsing stay
// consistent with that pattern.
export interface BoxCulvertConfigurableField {
  suffix: string; // row id suffix, e.g. `bc_2_${suffix}`
  label: string;
  describe: (mm: number) => string;
}

export const BOX_CULVERT_CONFIGURABLE_FIELDS: BoxCulvertConfigurableField[] = [
  { suffix: 'height', label: 'Height (m)', describe: (mm) => `${(mm / 1000).toFixed(3)}` },
  { suffix: 'concBlinding', label: 'Concrete Blinding (mm)', describe: (mm) => `Concrete blinding ${mm}mm thick` },
  { suffix: 'bedTop', label: 'Pipe Bedding Top & Bottom (mm)', describe: (mm) => `Pipe bedding ${mm}mm thick top & bottom` },
  { suffix: 'bedSide', label: 'Pipe Bedding Sides (mm)', describe: (mm) => `Pipe bedding ${mm}mm thick sides` },
  // FP is a percentage, not a thickness, and decimals are allowed (e.g. 4.5) —
  // still stored in the same generic Record<string, number> map as the mm
  // fields above (the map holds "whatever number this row was configured
  // with", not literally millimetres).
  { suffix: 'purchase', label: 'FP (%)', describe: (pct) => `Purchase price of culvert incl ${pct}% FP` },
  // Feed the specHdr sub-header's "x mm ... xxxm dp" text (see
  // makeBoxCulvertRows) — neither is used as a standalone row description of
  // its own, and specDepth is deliberately its own field rather than reusing
  // bc_${n}_height's value — confirmed the two aren't the same figure.
  { suffix: 'specSize', label: 'Box Culvert Size (mm)', describe: (mm) => `${mm}` },
  { suffix: 'specDepth', label: 'Box Culvert Depth (m)', describe: (mm) => `${(mm / 1000).toFixed(3)}` },
];

// No default value: matches stormBuildUpPonds.ts's convention — an unconfigured
// row keeps showing its static "xxx" placeholder text, only switching to the
// real mm value once that culvert's Configure modal sets one > 0.
const BOX_CULVERT_HEIGHT_PLACEHOLDER = 'xxx';
const BOX_CULVERT_CONC_BLINDING_PLACEHOLDER = 'Concrete blinding xxxmm thick';
const BOX_CULVERT_BED_TOP_PLACEHOLDER = 'Pipe bedding xxxmm thick top & bottom';
const BOX_CULVERT_BED_SIDE_PLACEHOLDER = 'Pipe bedding xxxmm thick sides';
const BOX_CULVERT_PURCHASE_PLACEHOLDER = 'Purchase price of culvert incl xxx% FP';

// Backfill Type is a dropdown, not a thickness — kept separate from the mm
// registry above (its own Record<string, string> config map, own describe).
export const BOX_CULVERT_BACKFILL_TYPE_OPTIONS = ['Mot Type 1', 'R/C 6F2', 'Q 6F5', 'As Dug'];
const BOX_CULVERT_BACKFILL_PLACEHOLDER = 'Backfill (xxx Under Carriageway)';
export const describeBoxCulvertBackfill = (type: string): string => `Backfill (${type} Under Carriageway)`;

// rowId is the full `bc_<n>_<suffix>` id — parses out the suffix and looks up
// its registry entry, if any (mirrors stormBuildUpPonds.ts's getPondConfigurableField).
export const getBoxCulvertConfigurableField = (rowId: string): BoxCulvertConfigurableField | undefined => {
  const match = rowId.match(/^bc_\d+_(.+)$/);
  if (!match) return undefined;
  return BOX_CULVERT_CONFIGURABLE_FIELDS.find((f) => f.suffix === match[1]);
};

const makeBoxCulvertRows = (
  n: number,
  culvertConfigMm: Record<string, number> = {},
  culvertBackfillType: Record<string, string> = {}
): StormBuildUpRowDef[] => {
  const heightMm = culvertConfigMm[`bc_${n}_height`];
  const concBlindingMm = culvertConfigMm[`bc_${n}_concBlinding`];
  const bedTopMm = culvertConfigMm[`bc_${n}_bedTop`];
  const bedSideMm = culvertConfigMm[`bc_${n}_bedSide`];
  const backfillType = culvertBackfillType[`bc_${n}_backfill`];
  const purchaseFpPct = culvertConfigMm[`bc_${n}_purchase`];
  const specSizeMm = culvertConfigMm[`bc_${n}_specSize`];
  const specDepthMm = culvertConfigMm[`bc_${n}_specDepth`];
  return [
    { id: `bc_${n}_hdr`,       description: `Culvert ${n}`,                                      unit: '',     defaultQuant: null, isHeader: true },
    // Two independent inputs feed this one line — Size and Depth are their
    // own fields, separate from bc_${n}_height (not the same figure).
    { id: `bc_${n}_specHdr`,   description: `${specSizeMm ? specSizeMm : 'xxx'}mm Box Culvert - ${specDepthMm ? (specDepthMm / 1000).toFixed(3) : 'xxx'}m dp`, unit: '', defaultQuant: null, isSubHeader: true },
    { id: `bc_${n}_height`,    description: heightMm ? BOX_CULVERT_CONFIGURABLE_FIELDS[0].describe(heightMm) : BOX_CULVERT_HEIGHT_PLACEHOLDER, unit: '', defaultQuant: null, itemLabel: 'Height', noQuant: true },
    { id: `bc_${n}_excavate`,  description: 'Excavate',                                          unit: 'm3',   defaultQuant: null },
    { id: `bc_${n}_earthwork`, description: 'Earthwork support',                                 unit: 'm',    defaultQuant: null },
    { id: `bc_${n}_compact`,   description: 'Compact Base of Trench',                            unit: 'm2',   defaultQuant: null },
    { id: `bc_${n}_deposit`,   description: 'Deposit on site',                                   unit: 'm3',   defaultQuant: null },
    { id: `bc_${n}_concBlinding`, description: concBlindingMm ? BOX_CULVERT_CONFIGURABLE_FIELDS[1].describe(concBlindingMm) : BOX_CULVERT_CONC_BLINDING_PLACEHOLDER, unit: 'm2', defaultQuant: null },
    { id: `bc_${n}_bedTop`,    description: bedTopMm ? BOX_CULVERT_CONFIGURABLE_FIELDS[2].describe(bedTopMm) : BOX_CULVERT_BED_TOP_PLACEHOLDER, unit: 'm3', defaultQuant: null },
    { id: `bc_${n}_bedSide`,   description: bedSideMm ? BOX_CULVERT_CONFIGURABLE_FIELDS[3].describe(bedSideMm) : BOX_CULVERT_BED_SIDE_PLACEHOLDER, unit: 'm3', defaultQuant: null },
    { id: `bc_${n}_backfill`,  description: backfillType ? describeBoxCulvertBackfill(backfillType) : BOX_CULVERT_BACKFILL_PLACEHOLDER, unit: 'm3', defaultQuant: null },
    { id: `bc_${n}_purchase`,  description: purchaseFpPct ? BOX_CULVERT_CONFIGURABLE_FIELDS[4].describe(purchaseFpPct) : BOX_CULVERT_PURCHASE_PLACEHOLDER, unit: 'm', defaultQuant: null },
    { id: `bc_${n}_labour`,    description: 'Labour & Plant - Install culverts',                 unit: 'Days', defaultQuant: null },
  ];
};

export const DEFAULT_BOX_CULVERT_SECTIONS = 1;
export const MAX_BOX_CULVERT_SECTIONS = 15;

export const getBoxCulvertsRows = (
  sectionCount: number,
  culvertConfigMm: Record<string, number> = {},
  culvertBackfillType: Record<string, string> = {}
): StormBuildUpRowDef[] =>
  Array.from({ length: sectionCount }, (_, i) => {
    const n = i + 1;
    return makeBoxCulvertRows(n, culvertConfigMm, culvertBackfillType);
  }).flat();

// Full fixed set — kept for parity with the other stormBuildUp* modules.
export const BOX_CULVERTS_ROWS: StormBuildUpRowDef[] = getBoxCulvertsRows(MAX_BOX_CULVERT_SECTIONS);

// Shifts a row-id-keyed map's `bc_<n>_*` keys down by one wherever n >
// deletedN, and drops the deleted section's own keys — mirrors
// shiftConcAttenuationSectionKeys/shiftCellularAttenuationSectionKeys.
export const shiftBoxCulvertSectionKeys = <T,>(map: Record<string, T>, deletedN: number): Record<string, T> => {
  const next: Record<string, T> = {};
  Object.entries(map).forEach(([id, value]) => {
    const match = id.match(/^bc_(\d+)_(.+)$/);
    if (!match) {
      next[id] = value;
      return;
    }
    const idx = Number(match[1]);
    if (idx === deletedN) return;
    const newIdx = idx > deletedN ? idx - 1 : idx;
    next[`bc_${newIdx}_${match[2]}`] = value;
  });
  return next;
};
